import { useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { get, type Booking, type BookingList } from "../api.js";
import { LessonActionDrawer } from "../calendar/LessonActionDrawer.js";
import { diaryEntries, type DiaryEntry } from "../calendar/entries.js";
import { dayOf, sessionsByColumn, windowFor, type Session } from "../calendar/grid.js";
import { formatDate, formatTime } from "../format.js";

/**
 * Kept as a compatibility type for the student-page callers while the old
 * page-owned inline panel state disappears. The row itself no longer decides
 * move/cancel policy: every action now lives in `LessonActionDrawer`.
 */
export type LessonActionKind = "cancel" | "move";

export interface OpenLessonAction {
  appointmentId: number;
  kind: LessonActionKind;
}

export function isLessonActionOpen(
  open: OpenLessonAction | null,
  appointmentId: number,
  kind: LessonActionKind,
): boolean {
  return open !== null && open.appointmentId === appointmentId && open.kind === kind;
}

export interface OnBehalfLessonContext {
  /** The student the already-authorised office surface is acting for. */
  studentId: string;
  endsAt: string;
  status: string;
  /** Other active people on the same Amelia appointment. */
  sharedWith: number;
  tutorId: string | null;
  tutorName: string | null;
  externalProviderId: number | null;
}

interface OpenDrawer {
  session: Session<DiaryEntry>;
  participantCount?: number;
  showNotes: boolean;
}

/**
 * Resolve one student-record row back through the same scoped diary read that
 * supplies `LessonActionDrawer` on `/diary`.
 *
 * The student projection intentionally does not duplicate booking ids or live
 * payment identity. Looking the appointment up here gives the drawer its
 * authoritative Hub booking ids (for notes), student id (for payment/credit),
 * and the current appointment shape without manufacturing any of them from the
 * student row. It also preserves the diary's server-side visibility boundary:
 * a colleague appointment that is anonymous in an ordinary tutor's diary
 * cannot be turned back into a named, manageable session here.
 */
export async function managedLessonSession(
  ameliaAppointmentId: number,
  startsAt: string,
): Promise<Session<DiaryEntry> | null> {
  const window = windowFor("day", dayOf(startsAt));
  const diary = await get<BookingList>("/admin/bookings", window);
  const matching = diaryEntries(diary, { includeNoShows: true }).filter(
    (entry): entry is DiaryEntry & { anonymous: false } =>
      !entry.anonymous && entry.externalId === ameliaAppointmentId,
  );
  if (matching.length === 0) return null;

  return sessionsByColumn(matching, ["appointment"], () => "appointment").get("appointment")?.[0] ?? null;
}

/**
 * A deliberately limited fallback for the existing office/on-behalf surface.
 *
 * A non-owner with `appointments.manage.all` is allowed to act for another
 * tutor in `OfficeTutorBookingPanel`, but the diary quite correctly keeps that
 * colleague's student identity anonymous. The office panel already has the
 * student and future-lesson identity from its separately authorised student
 * read, so it may supply this context explicitly. We build only the fields the
 * shared drawer needs for Move/Cancel/payment. `showNotes` is false for this
 * fallback because colleague lesson notes have their own, narrower read policy.
 *
 * Ordinary student rows never receive this context, so failing the named diary
 * lookup remains a hard stop for them.
 */
export function onBehalfLessonSession(
  ameliaAppointmentId: number,
  studentName: string,
  startsAt: string,
  serviceName: string | null,
  context: OnBehalfLessonContext,
): Session<DiaryEntry> {
  const booking: Booking = {
    appointmentId: `amelia:${ameliaAppointmentId}`,
    externalId: ameliaAppointmentId,
    startsAt,
    endsAt: context.endsAt,
    serviceName,
    categoryName: null,
    status: context.status,
    isSchools: false,
    externalProviderId: context.externalProviderId,
    bookingId: `on-behalf:${ameliaAppointmentId}:${context.studentId}`,
    bookingStatus: context.status,
    price: null,
    customerName: studentName,
    studentId: context.studentId,
    studentName,
    attributionSource: null,
    tutorId: context.tutorId,
    tutorName: context.tutorName,
    tutorColour: null,
    instrument: null,
    lessonId: null,
    attendanceStatus: null,
    lessonPublishedAt: null,
  };
  const entry: DiaryEntry = { anonymous: false, ...booking };
  const session = sessionsByColumn([entry], ["appointment"], () => "appointment").get("appointment")?.[0];
  if (!session) throw new Error("Could not build the appointment details.");
  return session;
}

function nestedInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest("a, button, input, select, textarea, label") !== null;
}

/**
 * One upcoming private lesson on a student record.
 *
 * This component is deliberately only an adapter now: the row opens the same
 * `LessonActionDrawer` as the diary, and the drawer owns Reschedule, Cancel,
 * live payment classification, mark-paid/apply-credit, and notes. Hub-native
 * lessons have no Amelia appointment id, remain non-actionable here, and keep
 * their specialist workflow.
 */
export function ManageLessonRow({
  appointmentId,
  canManage,
  studentName,
  startsAt,
  serviceName,
  onBehalf,
  onChanged,
  children,
}: {
  /** Amelia's numeric id, or null for a Hub-native/specialist lesson. */
  appointmentId: number | null;
  /** Host-level authority. Backend mutation routes still re-check it. */
  canManage: boolean;
  studentName: string;
  startsAt: string;
  serviceName: string | null;
  /** Only the existing office/on-behalf host may provide this fallback. */
  onBehalf?: OnBehalfLessonContext;
  /** Legacy page-owned action state, ignored now that the drawer owns it. */
  open?: OpenLessonAction | null;
  onOpen?: (next: OpenLessonAction | null) => void;
  busy?: boolean;
  onBusyChange?: (busy: boolean) => void;
  /** Re-read the page after an authoritative mutation. */
  onChanged: () => void;
  children: ReactNode;
}) {
  const [drawer, setDrawer] = useState<OpenDrawer | null>(null);
  const [opening, setOpening] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const row = useRef<HTMLDivElement>(null);
  const actionable = canManage && appointmentId !== null;

  async function openDrawer() {
    if (!actionable || appointmentId === null || opening) return;
    // Mouse activation does not consistently focus a div with button semantics.
    // Establish the originating row as the focus-return target before the async
    // diary read opens the shared modal.
    row.current?.focus({ preventScroll: true });
    setOpening(true);
    setProblem(null);
    try {
      const current = await managedLessonSession(appointmentId, startsAt);
      if (current !== null) {
        setDrawer({ session: current, showNotes: true });
        return;
      }
      if (onBehalf !== undefined) {
        setDrawer({
          session: onBehalfLessonSession(
            appointmentId,
            studentName,
            startsAt,
            serviceName,
            onBehalf,
          ),
          participantCount: Math.max(1, onBehalf.sharedWith + 1),
          showNotes: false,
        });
        return;
      }
      setProblem("This appointment is not available in your managed diary scope.");
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "We could not open this appointment right now.");
    } finally {
      setOpening(false);
    }
  }

  function clickRow(event: ReactMouseEvent<HTMLDivElement>) {
    if (nestedInteractiveTarget(event.target)) return;
    void openDrawer();
  }

  function keyRow(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (nestedInteractiveTarget(event.target)) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    void openDrawer();
  }

  function changed() {
    setDrawer(null);
    onChanged();
  }

  return (
    <div>
      <div
        ref={row}
        className="slot"
        role={actionable ? "button" : undefined}
        tabIndex={actionable ? 0 : undefined}
        aria-haspopup={actionable ? "dialog" : undefined}
        aria-label={actionable ? `Open ${serviceName ?? "lesson"} appointment for ${studentName}` : undefined}
        aria-busy={actionable ? opening : undefined}
        onClick={actionable ? clickRow : undefined}
        onKeyDown={actionable ? keyRow : undefined}
        style={actionable ? { cursor: opening ? "progress" : "pointer" } : undefined}
      >
        <time>{formatTime(startsAt)}</time>
        <div style={{ flex: 1 }}>
          <div>{formatDate(startsAt)}</div>
          {children}
        </div>
        {actionable && (
          <span className="small muted" aria-hidden="true">
            {opening ? "Opening…" : "›"}
          </span>
        )}
      </div>

      {problem && <div className="error small" style={{ marginTop: 8 }}>{problem}</div>}

      {drawer && (
        <LessonActionDrawer
          key={`${drawer.session.appointmentId}:${appointmentId ?? "specialist"}`}
          session={drawer.session}
          canManage={canManage}
          participantCount={drawer.participantCount}
          showNotes={drawer.showNotes}
          onClose={() => setDrawer(null)}
          onChanged={changed}
        />
      )}
    </div>
  );
}
